import * as Dialog from "@radix-ui/react-dialog";
import { Cross2Icon } from "@radix-ui/react-icons";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "./ui/select";
import { currencies } from "src/helpers/countriesWithCurrencies";
import { WalletContainer, TitleDescription } from "../Styles";
import { Button } from "./ui/button";
import { Input } from "./ui/input";
import { FormProvider, useForm } from "react-hook-form";
import { FormItem } from "./ui/form";
import useReactForm from "../../hooks/useForm";
import validate from "src/helpers/validate";
import {
  useCreateTradeRequestMutationApi,
} from "src/hooks/useTrading";
import { useUserData } from "src/hooks/useUserData";
import { useEffect, useState } from "react";
import { useMutation } from "@tanstack/react-query";
import { updateUser } from "src/services";
import { useToast } from "./ui/use-toast";
import ReactSelect from "react-select";
import LoadingIndicator from "./LoadingIndicator";
import { useCreateBusinessTradeRequestMutationApi } from "src/hooks/useB2bTrading";

const tradeTypeOptions = [{ label: "Sell", value: "SELL" }];

const DEFAULT_PAYMENT_TIME_LIMIT_MIN = 15;
const DEFAULT_PAYMENT_TIME_INTERVAL = "MINUTES";

type AddTradeRequestProps = {
  open: boolean;
  handleClose: () => void;
  countries?: any;
  countriesIsLoading: boolean;
  isEdit?: boolean;
  tradeRequest?: any;
  businessId?: any;
  businessName?: string;
};

const AddTradeRequest = ({
  open,
  handleClose,
  countries,
  countriesIsLoading: _countriesIsLoading,
  tradeRequest,
  businessId,
  businessName,
}: AddTradeRequestProps) => {
  const { values, setValues, handleChange } = useReactForm(validate);

  const [tradeName, setTradeName] = useState("");

  const [selectedCurrency, setSelectedCurrencey] = useState<any>([]);

  const isEditState = Boolean(tradeRequest);
  const isBusiness = Boolean(businessId);

  useEffect(() => {
    const tradeCurrencies = currencies.filter((currency) =>
      tradeRequest?.currency_codes?.includes(currency.value),
    );
    if (isEditState) {
      setValues({
        tradeType: tradeRequest?.trade_type,
        amount: tradeRequest?.amount,
        min_amount: tradeRequest?.min_amount_usd,
        maxAmount: tradeRequest?.max_amount_usd,
        conversionRate: tradeRequest?.conversion_rate_usd,
        note: tradeRequest?.note,
      });
      setSelectedCurrencey(tradeCurrencies);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isEditState]);

  const requestCurrencies = selectedCurrency.map((filter: any) => filter.value);

  const handleTypeSelect = (event: any) => {
    setValues((prevValues: any) => ({
      ...prevValues,
      tradeType: event,
    }));
  };

  const { userData } = useUserData();
  const { createRequest, editRequest } = useCreateTradeRequestMutationApi();
  const { createBusinessRequest, editBusinessRequest } = useCreateBusinessTradeRequestMutationApi();

  const { toast } = useToast();

  const createTradingProfile = !userData?.trading_name;

  const createRequestPayload = {
    trade_type: values.tradeType,
    amount: values.amount,
    min_amount_usd: values.min_amount,
    max_amount_usd: values.maxAmount,
    conversion_rate_usd: values.conversionRate,
    currency_codes: requestCurrencies,
    region: userData?.country_code,
    notes: values.notes,
  };

  function handleUserCreateTradeRequest() {
    const payload = {
      ...createRequestPayload,
      trading_name: userData?.trading_name,
      payment_time_limit_in_min:
        tradeRequest?.payment_time_limit_in_min || DEFAULT_PAYMENT_TIME_LIMIT_MIN,
    };
    isEditState
      ? editRequest.mutate({ requestId: tradeRequest?.id, payload: payload })
      : createRequest.mutate(payload);
  }

  function handleCreateBusinessRequest() {
    const payload = {
      ...createRequestPayload,
      business_id: businessId,
      business_name: businessName,
      payment_time_limit_interval:
        tradeRequest?.payment_time_limit_interval || DEFAULT_PAYMENT_TIME_INTERVAL,
      payment_time_limit_time:
        tradeRequest?.payment_time_limit_time || DEFAULT_PAYMENT_TIME_LIMIT_MIN,
    };

    isEditState
      ? editBusinessRequest.mutate({ requestId: tradeRequest?.id, payload: payload })
      : createBusinessRequest.mutate(payload);
  }

  function handleCreateRequest() {
    if (isBusiness) {
      handleCreateBusinessRequest();
    } else {
      handleUserCreateTradeRequest();
    }
  }

  const createTradeName = useMutation({
    mutationFn: (payload: any) => updateUser(userData?.id, payload),
    onSuccess: () => {
      toast({
        variant: "default",
        description: "Your details were updated successfully.",
      });
    },
    onError: () => {
      toast({
        variant: "destructive",
        title: "Uh oh! Something went wrong.",
        description: "There was a problem with your request.",
      });
    },
  });

  function handleCreateTradeName() {
    createTradeName.mutate({ trading_name: tradeName });
  }

  const isLoading = createTradeName.isPending || createRequest.isPending;

  //Disables submit button
  const disableCreateProfile = !tradeName;

  const disableCreateRequest =
    !values.tradeType ||
    !values.amount ||
    !values.min_amount ||
    !values.maxAmount ||
    !values.conversionRate ||
    requestCurrencies?.length < 1;

  function handleCreateTradeProfile() {
    handleCreateTradeName();
  }

  useEffect(() => {
    if (
      createRequest.isSuccess ||
      editRequest.isSuccess ||
      createBusinessRequest.isSuccess ||
      editBusinessRequest.isSuccess
    ) {
      handleClose();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [
    createRequest.isSuccess,
    editRequest.isSuccess,
    createBusinessRequest.isSuccess,
    editBusinessRequest.isSuccess,
  ]);

  const methods = useForm();

  return (
    <Dialog.Root open={open}>
      <Dialog.Portal>
        <Dialog.Overlay className="DialogOverlay" />
        <Dialog.Content
          className="DialogContent"
          style={{
            width: "min(750px, calc(100vw - 2rem))",
            maxHeight: "calc(100vh - 2rem)",
          }}
        >
          {isLoading ? (
            <div className="flex items-center justify-center min-h-80">
              <LoadingIndicator />
            </div>
          ) : (
            <>
              <Dialog.Title style={{ marginBottom: 24 }} className="DialogTitle">
                {createTradingProfile ? "Add trade profile details" : "Create Sell Request"}
              </Dialog.Title>
              <Dialog.Close asChild>
                <button className="IconButton" aria-label="Close" onClick={handleClose}>
                  <Cross2Icon />
                </button>
              </Dialog.Close>
              <WalletContainer>
                {!createTradingProfile && (
                  <FormProvider {...methods}>
                    <FormItem>
                      <TitleDescription>Trading name</TitleDescription>
                      <Input
                        type="text"
                        id="name"
                        readOnly
                        defaultValue={isBusiness ? businessName : userData?.trading_name}
                      />
                    </FormItem>
                    <FormItem style={{ marginTop: 15 }}>
                      <TitleDescription>Amount(AKL)</TitleDescription>
                      <Input
                        type="text"
                        name="amount"
                        placeholder="0.00"
                        onChange={handleChange}
                        defaultValue={values.amount}
                      />
                    </FormItem>
                    <FormItem style={{ marginTop: 15 }}>
                      <TitleDescription>Trade type</TitleDescription>
                      <Select
                        name="tradeType"
                        value={values.tradeType}
                        onValueChange={(event) => handleTypeSelect(event)}
                      >
                        <SelectTrigger className="w-[100%]">
                          <SelectValue placeholder="Buy/Sell" />
                        </SelectTrigger>
                        <SelectContent>
                          {tradeTypeOptions?.map(({ value, label }) => (
                            <SelectItem key={value} value={value}>
                              {label}
                            </SelectItem>
                          ))}
                        </SelectContent>
                      </Select>
                    </FormItem>
                    <div
                      style={{
                        display: "flex",
                        alignItems: "center",
                        gap: 8,
                        marginTop: 15,
                        padding: "10px 14px",
                        backgroundColor: "#FEF3C7",
                        border: "1px solid #F59E0B",
                        borderRadius: 8,
                      }}
                    >
                      <span style={{ fontSize: 16 }}>⚠️</span>
                      <p style={{ margin: 0, fontSize: 13, color: "#92400E" }}>
                        The ADCB primary rate is <strong>1 AKL = 15.96 USD</strong>. Trade rates
                        cannot be less than this rate.
                      </p>
                    </div>
                    <div
                      style={{
                        display: "flex",
                        alignItems: "center",
                        justifyContent: "space-between",
                        marginTop: 15,
                      }}
                    >
                      <FormItem>
                        <TitleDescription>Conversion rate(USD)</TitleDescription>
                        <Input
                          type="number"
                          name="conversionRate"
                          placeholder="USD"
                          onChange={handleChange}
                          defaultValue={values.conversionRate}
                        />
                      </FormItem>
                      <FormItem>
                        <TitleDescription>Min amount(USD)</TitleDescription>
                        <Input
                          type="number"
                          name="min_amount"
                          placeholder="0.00"
                          onChange={handleChange}
                          defaultValue={values.min_amount}
                        />
                      </FormItem>
                      <FormItem>
                        <TitleDescription>Max amount(USD)</TitleDescription>
                        <Input
                          type="number"
                          name="maxAmount"
                          placeholder="0.00"
                          onChange={handleChange}
                          defaultValue={values.maxAmount}
                        />
                      </FormItem>
                    </div>
                    <FormItem style={{ marginTop: 15 }}>
                      <TitleDescription>Currencies</TitleDescription>
                      <ReactSelect
                        options={currencies}
                        formatOptionLabel={(option) => `${option.value} - ${option.label}`}
                        isClearable
                        isSearchable
                        isMulti
                        onChange={(option) => {
                          setSelectedCurrencey([...option]);
                        }}
                      />
                    </FormItem>
                    <FormItem style={{ marginTop: 15 }}>
                      <TitleDescription>Note</TitleDescription>
                      <Input
                        type="text"
                        name="notes"
                        placeholder="Note"
                        onChange={handleChange}
                        defaultValue={values.notes}
                      />
                    </FormItem>
                  </FormProvider>
                )}
                {createTradingProfile && (
                  <div>
                    <FormItem>
                      <TitleDescription>Trade name</TitleDescription>
                      <Input
                        type="text"
                        id="tradeName"
                        placeholder="eg. johnDoe"
                        onChange={(event) => {
                          setTradeName(event.target.value);
                        }}
                        defaultValue={tradeName}
                      />
                    </FormItem>
                  </div>
                )}
                </WalletContainer>
                <div
                  style={{
                    display: "flex",
                    alignItems: "center",
                    justifyContent: "end",
                    marginTop: 20,
                  }}
                >
                  <Button
                    onClick={createTradingProfile ? handleCreateTradeProfile : handleCreateRequest}
                    disabled={createTradingProfile ? disableCreateProfile : disableCreateRequest}
                    className="flex items-center justify-center py-1 rounded-md bg-red-500 px-6 text-sm font-semibold leading-6 text-white shadow-sm hover:bg-red-600 focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-gray-400 mt-2"
                  >
                    {createTradingProfile || isEditState ? "Save" : "Submit request"}
                  </Button>
                </div>
              </>
            )}
          </Dialog.Content>
      </Dialog.Portal>
    </Dialog.Root>
  );
};

export default AddTradeRequest;
